// Contexto puro do fluxo MAD (AD-001/AD-002): funções de regra recebem `(ctx, task)`
// e devolvem valores — nada de React aqui. `MadRuleContext` espelha o NormalRuleContext
// (duplicação INTENCIONAL — isolamento AD-001, nunca importar de normal/) e acrescenta
// `duplaIds`, a identidade da dupla de liderança que permeia todas as regras MAD
// (regras canônicas do cliente, 2026-07-15).

import { EvaluationStep, PlayerFields, TaskRecord } from '../shared/types';

// Etapa (mês) de um ciclo, como chega em `cyclesExtended[].steps` / `stages`.
// Datas em epoch millis; `type` distingue month/trimester/semester (OM-03/COT-01).
export interface CycleStep {
  _id?: string;
  title?: string;
  type?: string;
  status?: string;
  date_start?: number;
  date_finish?: number;
  date_evaluation_step2_start?: number;
  date_evaluation_step2_finish?: number;
  date_evaluation_step3_start?: number;
  date_evaluation_step3_finish?: number;
  date_evaluation_step4_start?: number;
  date_evaluation_step4_finish?: number;
  date_evaluation_pitStop_start?: number;
  date_evaluation_pitStop_finish?: number;
  date_evaluation_step5_start?: number;
  date_evaluation_step5_finish?: number;
  [key: string]: any;
}

// Ciclo estendido do useAuth (cyclesExtended), com seus steps internos.
export interface CycleExtended {
  _id: string;
  steps?: CycleStep[];
  date_start?: number;
  date_end?: number;
  [key: string]: any;
}

// Bônus "Dobra extra" carregado do backend (estado `bonus` do monólito).
export interface BonusInfo {
  hasBonus?: boolean;
  isUsed?: boolean;
  taskId?: string;
  stage?: number;
  action?: string;
  [key: string]: any;
}

export interface MadRuleContext {
  // Gestor dono da sessão de apreciação (player_fields__c). No monólito é o
  // estado `playerInfo` (gestor substituído, quando em modo férias).
  playerInfo: PlayerFields | undefined;
  // Usuário logado (substituto em modo férias). Estado `userInfo` do useAuth.
  userInfo: PlayerFields | undefined;
  // Usuário interno exibido na página (estado `internalUser`) — só a presença é
  // consultada pelas regras (guard de isCurrentEvaluator).
  internalUser: { _id: string; [key: string]: any } | undefined;
  // Hora do SERVIDOR em epoch millis (estado `realDatetime`; 0 = ainda não carregada).
  realDatetime: number;
  // Etapa (mês) selecionada no ciclo (memo `activeStageObj`).
  activeStageObj: CycleStep | undefined;
  // Janela de apreciação ativa (memo `getActiveEvaluationStep`) — aqui como VALOR.
  getActiveEvaluationStep: EvaluationStep;
  // Ciclos estendidos do useAuth (inclui steps month/trimester/semester).
  cyclesExtended: CycleExtended[] | undefined;
  // Ciclos "reais" do useAuth (realCycle) — getTaskStatuses resolve o step da task
  // por eles, não por cyclesExtended (UI-08).
  realCycle: CycleExtended[] | undefined;
  // true quando o logado atua como substituto de férias de outro gestor.
  isVacationManager: boolean;
  // Ids dos parceiros da dupla do gestor da sessão (POS-02/R-14/PIT-05). Derivado
  // de playerInfo.mad_dupla quando não informado; vazio = gestor sem dupla (SUC-08:
  // estado NORMAL, nunca anomalia).
  duplaIds: string[];
  // Cotas e universos calculados por fase (estados homônimos do monólito), sobre o
  // pool exclusivamente MAD (COT-10).
  doubledTasks: TaskRecord[];
  maxDoubledTasks: number;
  promotedTasks: TaskRecord[];
  maxPromotedTasks: number;
  // uau/mandouBem começam undefined no monólito (useState<any[]>() sem inicial) —
  // as regras testam Array.isArray, então o default preserva o comportamento.
  uauTasks: TaskRecord[] | undefined;
  maxUauTasks: number;
  mandouBemTasks: TaskRecord[] | undefined;
  maxMandouBemTasks: number;
  // Bônus "Dobra extra" (estado `bonus`).
  bonus: BonusInfo | undefined;
  // COT-06: o filtro de maxDoubledTasks usa a hora do CLIENTE (`+new Date()`),
  // não a do servidor — bug preservado por paridade e injetável para teste.
  clientNow: () => number;
}

export type MadRuleContextInput = Partial<MadRuleContext>;

// Ids dos parceiros da dupla do gestor. Elo sem `_id` é ignorado (mesmo guard
// `md._id && ...` dos ramos om-dupla do monólito).
export function duplaIds(playerInfo: PlayerFields | undefined): string[] {
  const madDupla = playerInfo?.mad_dupla;

  if (!Array.isArray(madDupla)) return [];

  return (madDupla as any[])
    .map((md: any) => md?._id)
    .filter(Boolean)
    .map(String);
}

// Monta o contexto com os mesmos defaults do estado React original; `duplaIds`
// deriva de playerInfo.mad_dupla quando não informado explicitamente.
export function buildMadContext(input: MadRuleContextInput = {}): MadRuleContext {
  return {
    playerInfo: input.playerInfo,
    userInfo: input.userInfo,
    internalUser: input.internalUser,
    realDatetime: input.realDatetime ?? 0,
    activeStageObj: input.activeStageObj,
    getActiveEvaluationStep: input.getActiveEvaluationStep ?? null,
    cyclesExtended: input.cyclesExtended,
    realCycle: input.realCycle,
    isVacationManager: input.isVacationManager ?? false,
    duplaIds: input.duplaIds ?? duplaIds(input.playerInfo),
    doubledTasks: input.doubledTasks ?? [],
    maxDoubledTasks: input.maxDoubledTasks ?? 0,
    promotedTasks: input.promotedTasks ?? [],
    maxPromotedTasks: input.maxPromotedTasks ?? 0,
    uauTasks: input.uauTasks,
    maxUauTasks: input.maxUauTasks ?? 0,
    mandouBemTasks: input.mandouBemTasks,
    maxMandouBemTasks: input.maxMandouBemTasks ?? 0,
    bonus: input.bonus,
    clientNow: input.clientNow ?? (() => Date.now()),
  };
}

// Nível efetivo do gestor no fluxo MAD (NIV-01..05) — port do `userCodEtapa` do
// monólito (index.tsx:258-311) com verifyMad SEMPRE true: no fluxo MAD a composição
// pela dupla é integral, não condicionada à task (NIV-02/03). Demais contratos
// preservados: sub composto vence se estritamente maior (NIV-04, redefinida em
// 2026-07-23: substituição oficial vale por si, sem o gate user.mad/isVacationManager
// do monólito); retorno CRU quando o próprio vence (NIV-05 — call-sites testam
// `=== 1 || === 3`).
export function madUserCodEtapa(ctx: MadRuleContext, userI?: any): number | undefined {
  if (!userI && !ctx.playerInfo) return undefined;

  const user = userI ?? ctx.playerInfo;

  const normalize = (val: number) => (typeof val === 'number' ? (val === 1 ? 3 : val > 5 ? 0 : val) : 0);

  const madCodEtapas = Array.isArray(user.mad_dupla)
    ? user.mad_dupla
        .map((m: any) => (typeof m.cod_etapa === 'number' ? normalize(m.cod_etapa) : null))
        .filter((v: any) => v !== null)
    : [];

  const maxMadCodEtapa = madCodEtapas.length > 0 ? Math.max(...madCodEtapas) : 0;

  const codEtapa =
    maxMadCodEtapa >
    (typeof user.cod_etapa === 'number' ? (user.cod_etapa === 1 ? 3 : user.cod_etapa > 5 ? 0 : user.cod_etapa) : 0)
      ? maxMadCodEtapa
      : typeof user.cod_etapa === 'number'
        ? user.cod_etapa
        : 0;

  const madCodEtapasSub = Array.isArray(user.mad_dupla)
    ? user.mad_dupla
        .map((m: any) => (typeof m.cod_etapa_sub === 'number' ? normalize(m.cod_etapa_sub) : null))
        .filter((v: any) => v !== null)
    : [];

  const maxMadCodEtapaSub = madCodEtapasSub.length > 0 ? Math.max(...madCodEtapasSub) : 0;

  const codEtapaSub =
    maxMadCodEtapaSub >
    (typeof user.cod_etapa_sub === 'number'
      ? user.cod_etapa_sub === 1
        ? 3
        : user.cod_etapa_sub > 5
          ? 0
          : user.cod_etapa_sub
      : 0)
      ? maxMadCodEtapaSub
      : typeof user.cod_etapa_sub === 'number'
        ? user.cod_etapa_sub
        : 0;

  return (codEtapaSub === 1 ? 3 : codEtapaSub) > (codEtapa === 1 ? 3 : codEtapa) ? codEtapaSub : codEtapa;
}

// prosseguir_gst COMPOSTO pela dupla (NIV-06, regra canônica 2026-07-09): se o
// gestor OU qualquer parceiro da mad_dupla tem prosseguir_gst 1, a dupla tem 1.
// Na base de players prosseguir_gst é flag numérica 0|1 (difere do campo homônimo
// da task, string 'next'|'stopped'|'approved').
export function prosseguirGstComposto(manager: any): 0 | 1 {
  if (!manager) return 0;

  if (manager.prosseguir_gst === 1) return 1;

  if (Array.isArray(manager.mad_dupla) && manager.mad_dupla.some((md: any) => md?.prosseguir_gst === 1)) {
    return 1;
  }

  return 0;
}

// cod_etapa efetivo COMPOSTO e NORMALIZADO (regra decorada 2026-07-15, base do
// PIT-01b): máximo normalizado (1→3; >5→0, incl. sentinela 99) entre cod_etapa e
// cod_etapa_sub do gestor E de TODOS os parceiros mad_dupla. Diferente do
// madUserCodEtapa (paridade com o monólito, retorno cru), aqui o retorno é SEMPRE
// normalizado — é a régua canônica da composição, não um port.
export function codEtapaComposto(manager: any): number {
  if (!manager) return 0;

  const normalize = (val: any) => (typeof val === 'number' ? (val === 1 ? 3 : val > 5 ? 0 : val) : 0);

  const values = [normalize(manager.cod_etapa), normalize(manager.cod_etapa_sub)];

  if (Array.isArray(manager.mad_dupla)) {
    for (const md of manager.mad_dupla) {
      values.push(normalize(md?.cod_etapa));
      values.push(normalize(md?.cod_etapa_sub));
    }
  }

  return Math.max(...values);
}

// Task pertence ao universo MAD (critério A2): exclusivamente `mad === true`.
// O critério legado por non_evaluator (isMAD do monólito) NÃO vale aqui.
export function isMadTask(task: any): boolean {
  return !!task && task.mad === true;
}

// Gestor/funcionário é COE (COE-01..04): tipo_dependencia normalizado. COE nunca
// tem gestor_2/mad_dupla e aprecia normalmente como gestor único (COE-04).
export function isCoe(manager: any): boolean {
  return typeof manager?.tipo_dependencia === 'string' && manager.tipo_dependencia.trim().toUpperCase() === 'COE';
}

// uor_linha com isenção de dupla (regra 2026-07-21) — o backend (scheduler 65b4214f)
// aplica a mesma lista na geração de duplas/gestor_2.
export const UOR_LINHA_SEM_DUPLA = [534734, 534739, 551080];

// Exceção de DUPLA (2026-07-21): COE OU uor_linha isento nunca participam de dupla.
// A isenção é SOMENTE de dupla — o pit stop continua restrito a COE (COE-03), por
// isso pitstop.ts segue usando isCoe. uor_linha chega como número do backend, mas
// aceita string numérica por robustez.
export function isSemDupla(manager: any): boolean {
  if (isCoe(manager)) return true;

  const uorLinha = Number(manager?.uor_linha);

  return Number.isFinite(uorLinha) && UOR_LINHA_SEM_DUPLA.includes(uorLinha);
}
